import { useState, useEffect } from "react";
import { Cloud, Sun, Droplets, Wind, Thermometer, Loader2 } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEATHER_ICONS = {
  0: Sun, 1: Sun, 2: Cloud, 3: Cloud,
  45: Cloud, 48: Cloud, 51: Droplets, 53: Droplets,
  55: Droplets, 61: Droplets, 63: Droplets, 65: Droplets,
  71: Cloud, 73: Cloud, 75: Cloud, 80: Droplets, 81: Droplets,
  82: Droplets, 95: Cloud, 96: Cloud, 99: Cloud,
};

const WEATHER_TEXT = {
  0: "Ciel dégagé", 1: "Peu nuageux", 2: "Partiellement nuageux", 3: "Couvert",
  45: "Brouillard", 48: "Brouillard givrant", 51: "Bruine légère", 53: "Bruine",
  55: "Bruine forte", 61: "Pluie légère", 63: "Pluie modérée", 65: "Pluie forte",
  71: "Neige légère", 73: "Neige", 75: "Neige forte",
  80: "Averses légères", 81: "Averses", 82: "Averses violentes",
  95: "Orage", 96: "Orage grêle", 99: "Orage grêle forte",
};

export default function WeatherWidget({ lat = -21.1151, lon = 55.5364 }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/agro/weather?lat=${lat}&lon=${lon}`)
      .then(r => r.json())
      .then(setWeather)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lat, lon]);

  if (loading) return (
    <div className="floating-panel p-2.5 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#2D6A4F" }} />
      <span className="text-[10px]" style={{ color: "#7A7A7A" }}>Météo...</span>
    </div>
  );

  if (!weather?.current) return null;

  const cur = weather.current;
  const code = cur.weather_code || 0;
  const WeatherIcon = WEATHER_ICONS[code] || Cloud;
  const desc = WEATHER_TEXT[code] || "—";

  return (
    <div className="floating-panel p-2.5 space-y-1.5 min-w-[140px]" data-testid="weather-widget">
      <div className="flex items-center gap-2">
        <WeatherIcon className="w-5 h-5" style={{ color: "#2D6A4F" }} />
        <div>
          <p className="text-sm font-bold leading-tight" style={{ color: "#1A1A1A" }}>{cur.temperature}°C</p>
          <p className="text-[9px]" style={{ color: "#7A7A7A" }}>{desc}</p>
        </div>
      </div>
      <div className="flex gap-3 text-[9px]" style={{ color: "#4A4A4A" }}>
        <span className="flex items-center gap-0.5"><Droplets className="w-3 h-3" />{cur.humidity}%</span>
        <span className="flex items-center gap-0.5"><Wind className="w-3 h-3" />{cur.wind_speed} km/h</span>
      </div>
      {weather.today_sunshine_hours > 0 && (
        <div className="flex items-center gap-1 text-[9px]" style={{ color: "#D4A373" }}>
          <Sun className="w-3 h-3" />{weather.today_sunshine_hours}h ensoleillement
        </div>
      )}
      {weather.forecast?.length > 0 && (
        <div className="flex gap-1 pt-1 border-t border-black/5 overflow-x-auto">
          {weather.forecast.slice(0, 4).map((d) => {
            const DayIcon = WEATHER_ICONS[d.weather_code] || Cloud;
            return (
              <div key={d.date || `${d.temp_max}-${d.weather_code}`} className="text-center min-w-[32px]">
                <p className="text-[8px]" style={{ color: "#7A7A7A" }}>{d.date?.slice(5)}</p>
                <DayIcon className="w-3 h-3 mx-auto" style={{ color: "#4A4A4A" }} />
                <p className="text-[8px] font-medium" style={{ color: "#1A1A1A" }}>{d.temp_max}°</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
